/**
 * DirectionAvatar — Identity glyph that swaps per visual language.
 *
 *   ATELIER  → italic gold monogram ("— ב —" in Hebrew, "— T —" in Latin)
 *              rendered as a corner-stamp at the top of the page.
 *   CONCIERGE → 40px circular avatar in the page header band, gradient
 *              hospitality-green, single initial of the tenant name.
 *
 * The two variants live in different parts of the page, so this component
 * absolute-positions itself when in 'atelier' and renders inline when in
 * 'concierge'. Parents do not need to reserve space for either.
 *
 * Initials are locale-aware: Hebrew tenants get the first Hebrew letter
 * (typically ת for TendedLoop, or the first character of the tenant's
 * Hebrew display name). Latin tenants get the Latin initial.
 */
import { useDirection } from '@/providers/DirectionProvider';

export interface DirectionAvatarProps {
  /** Tenant name — first character becomes the avatar initial. */
  tenantName: string;
  /** Optional: override the auto-derived initial (e.g. force "T" or "ת"). */
  initial?: string;
  /** Tone — affects size on the page header (default = 'lg' in Concierge). */
  size?: 'sm' | 'md' | 'lg';
}

/** Pick a single-glyph initial — works for Latin, Hebrew, Arabic, Cyrillic. */
function deriveInitial(name: string, override?: string): string {
  if (override) return override.slice(0, 1).toUpperCase();
  const trimmed = name.trim();
  if (!trimmed) return 'T';
  // Take the first non-whitespace character. For Hebrew/Arabic this is the
  // first letter of the right-to-left word, which is what we want.
  const first = Array.from(trimmed)[0] ?? 'T';
  // Hebrew/Arabic don't have case — toUpperCase is a no-op there.
  return first.toUpperCase();
}

export function DirectionAvatar({ tenantName, initial, size = 'lg' }: DirectionAvatarProps) {
  const direction = useDirection();
  const letter = deriveInitial(tenantName, initial);

  if (direction === 'atelier') {
    // Editorial monogram: a single gold letter framed by hairline em-dashes,
    // anchored to the top-right corner of whatever the parent is.
    return (
      <span
        className="pointer-events-none absolute top-3 end-4 text-[11px] font-serif italic"
        style={{
          color: 'var(--dir-monogram-color)',
          letterSpacing: '0.3em',
          opacity: 'var(--dir-monogram-shown)',
        }}
        aria-hidden="true"
        data-testid="direction-avatar-atelier"
      >
        — {letter} —
      </span>
    );
  }

  // Concierge avatar: solid gradient disc with the initial.
  const dimensions = { sm: 28, md: 36, lg: 44 }[size];
  const fontSize = { sm: 13, md: 15, lg: 17 }[size];

  return (
    <span
      className="inline-flex flex-shrink-0 items-center justify-center rounded-full font-medium"
      style={{
        width: dimensions,
        height: dimensions,
        fontSize,
        background:
          'linear-gradient(135deg, var(--dir-accent-primary), color-mix(in srgb, var(--dir-accent-primary) 75%, white))',
        color: 'var(--dir-accent-on)',
        boxShadow: '0 4px 14px color-mix(in srgb, var(--dir-accent-primary) 30%, transparent)',
        opacity: 'var(--dir-avatar-shown)',
      }}
      aria-hidden="true"
      data-testid="direction-avatar-concierge"
    >
      {letter}
    </span>
  );
}
